import { AuthStatus, Status } from "./useAuthorization";
import { ChatbotLockedContent, ErrorMessage, Loading, LockedContent } from "../ui/Authority";

import CalendarIcon from "../ui/CalendarIcon.png";
import MagnifyingGlassIcon from "../ui/MagnifyingGlassIcon.png";
/* eslint-disable no-duplicate-case */
import React from "react";
import lockIco from "../ui/lock_ico.png";
import { useQueryClient } from "react-query";

interface RenderOverlayProps {
    status: Status;
    missgoAIType?: "default" | "guide" | "guide-deep" | "chat" | "chat-statute";
    onPaymentButtonClick: (type?: "upgrade" | "ticket") => void;
    postFreeTrialMutate: (
        arg0: undefined,
        options?: {
            onSuccess?: () => void;
        },
    ) => void;
}

export const renderOverlay = ({
    status,
    missgoAIType,
    onPaymentButtonClick,
    postFreeTrialMutate,
}: RenderOverlayProps): React.ReactNode => {
    const queryClient = useQueryClient();

    const postFreeTrial = () => {
        postFreeTrialMutate(undefined, {
            onSuccess: async () => {
                await Promise.all([
                    queryClient.invalidateQueries({ queryKey: ["freeWhetherToUse"] }),
                    queryClient.invalidateQueries({ queryKey: ["getChatAvailableCount"] }),
                    queryClient.invalidateQueries({ queryKey: ["beforeChatLog"] }),
                    queryClient.invalidateQueries({ queryKey: ["summaryAuctionAI"] }),
                ]);
            },
        });
    };

    switch (status) {
        case AuthStatus.LOADING:
            return <Loading />;
        case AuthStatus.NOT_LOGGED_IN:
            return (
                <LockedContent
                    icon={lockIco}
                    buttonText="로그인 후 5일 무료체험"
                    onButtonClick={postFreeTrial}
                    textContent={
                        <>
                            <p>유료회원만 상세정보를</p>
                            <p>확인할 수 있어요</p>
                        </>
                    }
                />
            );
        case AuthStatus.NEEDS_FREE_TRIAL:
            if (missgoAIType === "guide-deep") {
                return (
                    <ChatbotLockedContent
                        icon={lockIco}
                        buttonText="VVIP Full 구독하기"
                        button2Text="1일간 무료체험하기"
                        onButtonClick={() => onPaymentButtonClick("upgrade")}
                        button2Click={postFreeTrial}
                        button2State={true}
                        textContent={
                            <>
                                <p>경매 길잡이 DEEP은 아래와 같은</p>
                                <p>기능을 제공해요!</p>
                            </>
                        }
                    />
                );
            }
            return (
                <LockedContent
                    icon={lockIco}
                    buttonText="1일간 무료체험하기"
                    onButtonClick={postFreeTrial}
                    hasSampleView={true}
                    textContent={
                        <>
                            <p>경매 길잡이는 아래와 같은</p>
                            <p>기능을 제공해요!</p>
                        </>
                    }
                />
            );
        case AuthStatus.NEEDS_SUBSCRIPTION:
            return (
                <LockedContent
                    icon={lockIco}
                    hasSampleView={true}
                    buttonText="VVIP Full 구독하기"
                    onButtonClick={() => onPaymentButtonClick("upgrade")}
                    textContent={
                        <>
                            <p>경매 길잡이는 아래와 같은</p>
                            <p>기능을 제공해요!</p>
                        </>
                    }
                />
            );
        case AuthStatus.ANALYSIS_IN_PROGRESS:
            return (
                <ErrorMessage
                    icon={MagnifyingGlassIcon}
                    alt="분석 중"
                    textContent={
                        <>
                            <p>미스고 AI가</p>
                            <p>
                                매물을 <span className="highLight">분석 중</span>입니다.
                            </p>
                        </>
                    }
                />
            );
        case AuthStatus.NEW_ITEM_ERROR:
            return (
                <ErrorMessage
                    icon={CalendarIcon}
                    alt="신건"
                    textContent={
                        <>
                            <p>해당 매물은 신건으로</p>
                            <span className="highLight">매각기일 7일 전부터</span>
                            <p>확인할 수 있습니다.</p>
                        </>
                    }
                />
            );
        case AuthStatus.NEEDS_CHATBOT_FREE_TRIAL:
            return (
                <LockedContent
                    icon={lockIco}
                    buttonText="1일간 무료체험하기"
                    onButtonClick={postFreeTrial}
                    textContent={
                        <>
                            <p>미스고 AI가</p>
                            <p>궁금하다면?</p>
                        </>
                    }
                    isChatbot={true}
                />
            );
        case AuthStatus.NEEDS_CHATBOT_SUBSCRIPTION:
            return (
                <LockedContent
                    icon={lockIco}
                    buttonText="VVIP Full 구독하러 하기"
                    onButtonClick={() => onPaymentButtonClick("upgrade")}
                    textContent={
                        <>
                            <p>미스고 AI가</p>
                            <p>궁금하다면?</p>
                        </>
                    }
                    isChatbot={true}
                />
            );
        case AuthStatus.NEEDS_CHATBOT_UPGRADE:
            return (
                <LockedContent
                    icon={lockIco}
                    buttonText="VVIP Full 구독권 업그레이드"
                    onButtonClick={() => onPaymentButtonClick("upgrade")}
                    textContent={
                        <>
                            <p>미스고 AI가</p>
                            <p>궁금하다면?</p>
                        </>
                    }
                    isChatbot={true}
                />
            );
        case AuthStatus.NEEDS_CHATBOT_TICKET_UPGRADE:
            return (
                <ChatbotLockedContent
                    icon={lockIco}
                    buttonText="AI 챗봇 이용권 구매"
                    button2Text={`VVIP Full 구독권 업그레이드`}
                    onButtonClick={() => onPaymentButtonClick("ticket")}
                    button2Click={() => onPaymentButtonClick("upgrade")}
                    textContent={
                        <>
                            <p>미스고 AI가</p>
                            <p>궁금하다면?</p>
                        </>
                    }
                    button2State={true}
                    isChatbot={true}
                    chatbotUpgradeNotice={true}
                />
            );
        case AuthStatus.NEEDS_CHATBOT_TICKET:
            return (
                <LockedContent
                    icon={lockIco}
                    buttonText="AI 챗봇 이용권 구매"
                    onButtonClick={() => onPaymentButtonClick("ticket")}
                    textContent={
                        <>
                            <p>미스고 AI가</p>
                            <p>궁금하다면?</p>
                        </>
                    }
                    isChatbot={true}
                />
            );
        case AuthStatus.SUCCESS:
            return null;
        default:
            return null;
    }
};
